import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Switch,
  Alert,
  Snackbar,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import { api } from "../context/AuthContext";
import { Search, FilterList } from '@mui/icons-material';

function SuperAdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchJobs = async () => {
    try {
      const response = await api.get('/super-admin/jobs');
      // Ensure we only set an array to the jobs state
      setJobs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setSnackbar({ open: true, message: 'Failed to fetch jobs', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = (job.title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (job.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (job.companyName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (job.location?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = statusFilter === 'All' ||
      (statusFilter === 'Active' && job.is_active) ||
      (statusFilter === 'Inactive' && !job.is_active);

    return matchesSearch && matchesFilter;
  });

  const paginatedJobs = filteredJobs.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredJobs.length / 10);

  const handleToggleStatus = async (jobId, currentStatus) => {
    try {
      await api.patch(`/super-admin/jobs/${jobId}/status`, { is_active: !currentStatus });
      setJobs(jobs.map(job => job.id === jobId ? { ...job, is_active: !currentStatus } : job));
      setSnackbar({ open: true, message: `Job ${!currentStatus ? 'activated' : 'inactivated'} successfully`, severity: 'success' });
    } catch (error) {
      console.error('Error updating job status:', error);
      setSnackbar({ open: true, message: 'Failed to update job status', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
            {/*<Typography
            sx={{
              color: "#1E3A8A",
              fontSize: {
                xs: "2rem",
                sm: "2.3rem",
                md: "2.6rem",
              },
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 0.8,
            }}
          >*/}
            <Typography
                variant="h4"
                sx={{
                    color: "#1E3A8A",
                    fontWeight: "bold",
                    fontSize: { xs: "1.5rem", md: "2rem" }
                }}
            >
            All Jobs
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              fontWeight: 400,
            }}
          >
            View and manage all jobs posted across the ExiHire platform.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              displayEmpty
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                  <FilterList sx={{ fontSize: 20 }} />
                  Filters
                </Box>
              )}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E3A8A',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E40AF',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                Filters
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Posted Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell sx={{ fontWeight: 'medium' }}>{job.title}</TableCell>
                <TableCell>{job.company_name || job.companyName}</TableCell>
                <TableCell>{job.location}</TableCell>
                <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip
                    label={job.is_active ? 'Active' : 'Inactive'}
                    color={job.is_active ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={job.is_active}
                      onChange={() => handleToggleStatus(job.id, job.is_active)}
                      color="primary"
                      size="small"
                    />
                    <Typography variant="caption">
                      {job.is_active ? "Active" : "Inactive"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No jobs found in the system.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
      >
        <Typography variant="body2" color="text.secondary">
          {paginatedJobs.length > 0 ? (page - 1) * 10 + 1 : 0}–
          {Math.min(page * 10, filteredJobs.length)} of{" "}
          {filteredJobs.length} records
        </Typography>
        <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SuperAdminJobs;
